'use client';

import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { faBarsFilter } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/ui/components/ui/button';
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerPortal,
	DrawerTitle
} from '@pickleballinc/ui/components/ui/drawer';
import { Icon } from '@pickleballinc/ui/components/ui/icon';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@pickleballinc/ui/components/ui/select';
import clsx from 'clsx';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useSeasonDistrictDivisionInfo } from '@/hooks/custom-hooks/useSeasonDistrictDivisionInfo';
import { useSeasonFromSearchParams } from '@/hooks/filters/useSeasonFromSearchParams';
import { Options } from '@/utils/types';

export interface ITeamDashboardFilters {
	season: string;
	district: string;
	division: string;
	mobile_filters: string;
}

interface TeamDashboardFiltersProps {
	seasons: Options[];
	districts: Options[];
	divisions: Options[];
	activeSeason: string;
}

const getNewSearchParams = (filters: ITeamDashboardFilters) => {
	const newSearchParams = new URLSearchParams(window.location.search);

	newSearchParams.delete('current_page');

	if (filters.season) {
		newSearchParams.set('season', filters.season);
	} else {
		newSearchParams.delete('season');
	}

	if (filters.district && filters.district !== 'all') {
		newSearchParams.set('district', filters.district);
	} else {
		newSearchParams.delete('district');
	}

	if (filters.division && filters.division !== 'all') {
		newSearchParams.set('division', filters.division);
	} else {
		newSearchParams.delete('division');
	}

	return newSearchParams || '';
};

export const TeamDashboardFilters = ({ seasons, activeSeason }: TeamDashboardFiltersProps) => {
	const router = useRouter();
	const searchParams = useSearchParams();
	const { districtOptionsWithAll, divisionOptionsWithAll, firstSeasonOptionUuid } = useSeasonDistrictDivisionInfo();

	const selectedDivisionOptionUuid = searchParams?.get('division') || divisionOptionsWithAll?.[0]?.value;
	const selectedDistrictOptionUuid = searchParams?.get('district') || districtOptionsWithAll?.[0]?.value;

	const [filters, setFilters] = useState<ITeamDashboardFilters>({
		season: searchParams?.get('season') ?? activeSeason ?? firstSeasonOptionUuid,
		district: selectedDistrictOptionUuid ?? '',
		division: selectedDivisionOptionUuid ?? '',
		mobile_filters: ''
	});

	const emptyFilters: ITeamDashboardFilters = {
		season: activeSeason ?? firstSeasonOptionUuid,
		district: districtOptionsWithAll?.[0]?.value ?? '',
		division: divisionOptionsWithAll?.[0]?.value ?? '',
		mobile_filters: ''
	};

	const handleReset = () => {
		setFilters(emptyFilters);
		router.replace(`?${getNewSearchParams(emptyFilters).toString()}`);
	};

	useSeasonFromSearchParams(searchParams, setFilters, activeSeason ?? seasons[0]?.value);

	useEffect(() => {
		const params = new URLSearchParams(searchParams.toString());
		const currentSeason = searchParams.get('season');

		if (currentSeason) return;

		const seasonUuid = activeSeason ?? firstSeasonOptionUuid;
		params.set('season', seasonUuid);

		router.replace(`?${params.toString()}`, { scroll: false });
	}, []);

	return (
		<>
			<div
				className={`${districtOptionsWithAll.length !== 2 ? 'hidden' : 'flex flex-col'} md:grid md:grid-cols-2 gap-3 md:gap-x-6 md:gap-y-4 mt-4 lg:mt-6 lg:mb-2`}
			>
				<div>
					<Select onValueChange={(value) => setFilters({ ...filters, season: value })} value={filters.season || seasons[0]?.value}>
						<SelectTrigger
							className="bg-white focus:ring-0 h-10"
							icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
						>
							<SelectValue placeholder="Select season" />
						</SelectTrigger>
						<SelectContent>
							{seasons.map((season) => (
								<SelectItem key={season.value} value={season.value}>
									{season.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className="hidden md:block" />

				<div className="flex items-start xl:items-center">
					<Select
						onValueChange={(value) => setFilters({ ...filters, district: value })}
						value={filters.district || districtOptionsWithAll[0]?.value}
					>
						<SelectTrigger
							id="district-select"
							className="bg-white focus:ring-0 h-10"
							icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
						>
							<SelectValue placeholder="Select district" />
						</SelectTrigger>
						<SelectContent>
							{districtOptionsWithAll.map((district) => (
								<SelectItem key={district.value} value={district.value}>
									{district.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className="flex flex-col xl:flex-row gap-3 items-start md:items-end xl:items-center w-full justify-between">
					<div className="self-start w-full">
						<Select
							onValueChange={(value) => setFilters({ ...filters, division: value })}
							value={filters.division || divisionOptionsWithAll[0]?.value}
						>
							<SelectTrigger
								id="division-select"
								className={clsx('bg-white focus:ring-0 h-10', {
									hidden: divisionOptionsWithAll.length === 2 // comparing if there are only 2 divisions (all and one)
								})}
								icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
							>
								<SelectValue placeholder="Select division" />
							</SelectTrigger>
							<SelectContent>
								{divisionOptionsWithAll.map((division) => (
									<SelectItem key={division.value} value={division.value}>
										{division.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="flex flex-row gap-4">
						<Button onClick={() => router.replace(`?${getNewSearchParams(filters)?.toString()}`)} size="lg">
							Apply
						</Button>

						<Button onClick={handleReset} size="lg" variant="outline">
							Reset
						</Button>
					</div>
				</div>
			</div>

			<Button
				variant="outline"
				className="flex animate-fade-in-down w-full items-center justify-center mt-5 md:hidden"
				size="lg"
				onClick={() => setFilters({ ...filters, mobile_filters: 'true' })}
			>
				<FontAwesomeIcon icon={faBarsFilter} className="mr-2" />
				Filters
			</Button>

			<MobileTeamDashboardFilters
				filters={filters}
				setFilters={setFilters}
				handleReset={handleReset}
				seasons={seasons}
				districts={districtOptionsWithAll}
				divisions={divisionOptionsWithAll}
			/>
		</>
	);
};

interface MobileTeamDashboardFiltersProps {
	filters: ITeamDashboardFilters;
	handleReset: () => void;
	setFilters: (filters: ITeamDashboardFilters) => void;
	seasons: Options[];
	districts: Options[];
	divisions: Options[];
}

export const MobileTeamDashboardFilters = ({ filters, handleReset, setFilters, seasons, districts, divisions }: MobileTeamDashboardFiltersProps) => {
	const router = useRouter();

	const isSliderOpen = filters.mobile_filters === 'true';

	return (
		<Drawer open={isSliderOpen} onClose={() => setFilters({ ...filters, mobile_filters: '' })}>
			<DrawerPortal>
				<DrawerContent className="flex w-full flex-col bg-white px-4 pb-4 pt-1 md:hidden h-[90%]">
					<DrawerDescription className="sr-only">Filters for teams dashboard</DrawerDescription>
					<div className="overflow-y-auto">
						<DrawerHeader className="flex justify-between">
							<DrawerTitle>Filters</DrawerTitle>
							<DrawerClose>
								<Icon glyph="close" />
							</DrawerClose>
						</DrawerHeader>
						<div className="flex flex-col gap-5">
							<div>
								<Select
									onValueChange={(value) => setFilters({ ...filters, season: value })}
									value={filters.season || seasons[0]?.value}
								>
									<SelectTrigger
										className="bg-white focus:ring-0 h-10"
										icon={({ className }: { className: string }) => (
											<FontAwesomeIcon icon={faChevronDown} className={className} />
										)}
									>
										<SelectValue placeholder="Select season" />
									</SelectTrigger>
									<SelectContent>
										{seasons.map((season) => (
											<SelectItem key={season.value} value={season.value}>
												{season.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="flex items-start xl:items-center">
								<Select
									onValueChange={(value) => setFilters({ ...filters, district: value })}
									value={filters.district || districts[0]?.value}
								>
									<SelectTrigger
										id="district-select-mobile"
										className="bg-white focus:ring-0 h-10"
										icon={({ className }: { className: string }) => (
											<FontAwesomeIcon icon={faChevronDown} className={className} />
										)}
									>
										<SelectValue placeholder="Select district" />
									</SelectTrigger>
									<SelectContent>
										{districts.map((district) => (
											<SelectItem key={district.value} value={district.value}>
												{district.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="flex flex-col xl:flex-row gap-3 items-start md:items-end xl:items-center w-full justify-between">
								<div className="self-start w-full">
									<Select
										onValueChange={(value) => setFilters({ ...filters, division: value })}
										value={filters.division || divisions[0]?.value}
									>
										<SelectTrigger
											id="division-select-mobile"
											className="bg-white focus:ring-0 h-10"
											icon={({ className }: { className: string }) => (
												<FontAwesomeIcon icon={faChevronDown} className={className} />
											)}
										>
											<SelectValue placeholder="Select division" />
										</SelectTrigger>
										<SelectContent>
											{divisions.map((division) => (
												<SelectItem key={division.value} value={division.value}>
													{division.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="flex flex-row gap-4 place-self-end">
									<Button
										onClick={() => {
											setFilters({ ...filters, mobile_filters: '' });
											router.replace(`?${getNewSearchParams(filters)?.toString()}`);
										}}
										size="lg"
									>
										Apply
									</Button>

									<Button onClick={handleReset} size="lg" variant="outline">
										Reset
									</Button>
								</div>
							</div>
						</div>
					</div>
				</DrawerContent>
			</DrawerPortal>
		</Drawer>
	);
};
